import React from "react";
import {
  Box,
  Typography,
  Grid,
  Link,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { fetchPopularFilmsOverview } from "../helpers/onCallFunctions.ts";
import { normaliseUpperCaseFilmTitle } from "../helpers/stringHelpers.ts";
import { StandardPageHead } from "../components/Heads/StandardPageHead.tsx";
import { PopularFilmOverview } from "../types/dataTypes.ts";
import { LoadingElementInBox } from "../components/Loading/LoadingElement.tsx";

export const PopularFilmsOverviewPage: React.FC = () => {
  const theme = useTheme();

  const [popularFilmOverview, setPopularFilmOverview] =
    React.useState<PopularFilmOverview>([]);
  const [isLoading, setIsLoading] = React.useState(true);

  const [expandedCities, setExpandedCities] = React.useState<Set<string>>(
    () => new Set(),
  );

  const toggleCity = React.useCallback((cityName: string) => {
    setExpandedCities((prev) => {
      const next = new Set(prev);
      if (next.has(cityName)) next.delete(cityName);
      else next.add(cityName);
      return next;
    });
  }, []);

  React.useEffect(() => {
    const load = async () => {
      const data = await fetchPopularFilmsOverview();
      setPopularFilmOverview(data);
      setIsLoading(false);
    };
    load();
  }, []);

  if (isLoading) return <LoadingElementInBox />;

  return (
    <>
      <StandardPageHead
        title="All Showings of every popular film near you"
        description="View All Popular Films With Listings on CinemaGuide. Quickly compare showtimes and buy cinema tickets."
        pageUrl="https://cinemaguideireland.com/popular-films"
      />
      <Typography
        variant="h1"
        component="h1"
        gutterBottom
        sx={{
          marginLeft: { xs: 0.5, md: 2 },
          marginRight: { xs: 0.5, md: 2 },
          paddingTop: { xs: 1, md: 1 },
          marginTop: 6,
          mb: { xs: 6, md: 10 },
          fontSize: { xs: "1.5rem", md: "2rem" },
          fontWeight: 500,
          textDecoration: "underline",
          textAlign: "center",
        }}
      >
        Popular Films Overview
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mx: "auto",
          width: { md: "80%", xs: "95%" },
          mt: { xs: 2, md: 4 },
          mb: 2,
          gap: 2,
        }}
      >
        {popularFilmOverview.map(({ cityName, films }) => {
          const cityLabel =
            cityName.charAt(0).toUpperCase() + cityName.slice(1);

          return (
            <Accordion
              key={cityName}
              disableGutters
              elevation={0}
              expanded={expandedCities.has(cityName)}
              onChange={() => toggleCity(cityName)}
              sx={{
                width: "100%",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                "&:before": { display: "none" },
                overflow: "hidden",
                fontSize: { xs: "1.2rem", md: "1.6rem" },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography
                  variant="h4"
                  sx={{
                    textAlign: "left",
                    fontSize: { xs: "1.1rem", md: "1.6rem" },
                    fontWeight: 500,
                  }}
                >
                  Popular films in {cityLabel}
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ mt: 2 }}>
                <Grid container spacing={{ xs: 1.5, sm: 2, md: 4 }}>
                  {films.map(({ display_film_title, title: filmSlug }) => (
                    <Grid
                      key={filmSlug}
                      item
                      xs={12}
                      sm={6}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      <Link
                        href={`popular-films/${cityName}/${filmSlug}`}
                        color={theme.palette.primary.main}
                        sx={{
                          mb: 2,
                          fontSize: { xs: "large", md: "x-large" },
                          "&:hover": { cursor: "pointer" },
                          textShadow: "0 0 4px rgba(0,160,255,.4)",
                        }}
                      >
                        {normaliseUpperCaseFilmTitle(display_film_title)}
                      </Link>
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    </>
  );
};
